import React from 'react';

// --- Reusable Sub-Components ---

/**
 * Gauge Component: Renders the speedometer-style performance level indicator.
 * @param {{level: 'BEGINNER'|'PROFICIENT'|'ADVANCED', checked: boolean}} props
 */
const Gauge = ({ level, checked }) => {
    // Determine the needle rotation based on the level.
    const getRotation = () => {
        switch (level) {
            case 'BEGINNER':
                return -60;
            case 'PROFICIENT':
                return 0;
            case 'ADVANCED':
                return 60;
            default:
                return 0;
        }
    };

    const rotation = checked ? getRotation() : 0; // Needle points to center if not checked

    return (
        <div className="flex flex-col items-center font-[poppins]">
            <svg width="100" height="60" viewBox="0 0 100 60">
                {/* Background Arcs */}
                <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#E5E7EB" strokeWidth="10" />
                <path d="M 10 50 A 40 40 0 0 1 30 17" fill="none" stroke="#EF4444" strokeWidth="10" />
                {/* Red */}
                <path d="M 30 17 A 40 40 0 0 1 70 17" fill="none" stroke="#FBBF24" strokeWidth="10" />
                {/* Yellow */}
                <path d="M 70 17 A 40 40 0 0 1 90 50" fill="none" stroke="#22C55E" strokeWidth="10" />
                {/* Green */}

                {/* Needle */}
                <line
                    x1="50"
                    y1="50"
                    x2="50"
                    y2="15"
                    stroke="#374151"
                    strokeWidth="3"
                    strokeLinecap="round"
                    transform={`rotate(${rotation}, 50, 50)`}
                    style={{ transition: 'transform 0.5s ease-in-out' }}
                />
                {/* Needle pivot point */}
                <circle cx="50" cy="50" r="5" fill="#374151" />
            </svg>
            <span className="text-xs font-bold mt-1">{level}</span>
        </div>
    );
};


/**
 * AbilitiesRow Component: Renders a row for Awareness, Sensitivity, or Creativity.
 * Uses inline styled checkboxes for PDF compatibility
 * @param {{label: string, levels: {beginner: boolean, proficient: boolean, advanced: boolean}}} props
 */
const AbilitiesRow = ({ label, levels }) => {
    const firstLetter = label.charAt(0);
    const restOfWord = label.slice(1);

    // Helper for PDF-compatible checkbox styling
    const getCheckboxStyle = (isChecked) => ({
        width: '16px',
        height: '16px',
        border: '2px solid #6B7280',
        borderRadius: '3px',
        backgroundColor: isChecked ? '#3B82F6' : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    return (
        <div className="grid grid-cols-5 items-center">
            <div className="col-span-2 text-sm">
                <span className="font-bold text-lg mr-1">{firstLetter}</span>{restOfWord}
            </div>
            <div className="flex justify-center">
                <div style={getCheckboxStyle(levels?.beginner || false)}>
                    {(levels?.beginner) && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                </div>
            </div>
            <div className="flex justify-center">
                <div style={getCheckboxStyle(levels?.proficient || false)}>
                    {(levels?.proficient) && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                </div>
            </div>
            <div className="flex justify-center">
                <div style={getCheckboxStyle(levels?.advanced || false)}>
                    {(levels?.advanced) && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                </div>
            </div>
        </div>
    );
};


/**
 * DomainSection Component: Renders a full block for one domain.
 * @param {{domainData: object}} props
 */
const DomainSection = ({ domainData }) => {
    // Find which level is checked for each ability to pass to the Gauge
    const getCheckedLevel = (ability) => {
        if (!ability) return null;
        if (ability.beginner) return 'BEGINNER';
        if (ability.proficient) return 'PROFICIENT';
        if (ability.advanced) return 'ADVANCED';
        return null;
    };

    const awarenessLevel = getCheckedLevel(domainData.awareness);
    const sensitivityLevel = getCheckedLevel(domainData.sensitivity);
    const creativityLevel = getCheckedLevel(domainData.creativity);

    return (
        <div className="mb-4 border border-gray-400">
            {/* Header */}
            <div className="flex">
                <div
                    className="w-1/3 bg-orange-500 text-white font-bold text-center py-2 flex items-center justify-center">
                    {domainData.domain_name}
                </div>
                <div className="w-2/3 border-l border-gray-400">
                    <div className="bg-orange-500 text-white font-bold text-center py-1 text-sm">
                        Performance Level Descriptors
                    </div>
                    <div className="grid grid-cols-3 gap-2 p-2 bg-white">
                        <Gauge level="BEGINNER"
                            checked={awarenessLevel === 'BEGINNER' || sensitivityLevel === 'BEGINNER' || creativityLevel === 'BEGINNER'} />
                        <Gauge level="PROFICIENT"
                            checked={awarenessLevel === 'PROFICIENT' || sensitivityLevel === 'PROFICIENT' || creativityLevel === 'PROFICIENT'} />
                        <Gauge level="ADVANCED"
                            checked={awarenessLevel === 'ADVANCED' || sensitivityLevel === 'ADVANCED' || creativityLevel === 'ADVANCED'} />
                    </div>
                </div>
            </div>

            {/* Abilities Table */}
            <div className="flex border-t border-gray-400">
                <div
                    className="w-1/3 bg-white text-center font-bold flex items-center justify-center text-sm p-2 border-r border-gray-400">
                    ABILITIES
                </div>
                <div className="w-2/3 bg-orange-50 p-2 space-y-2">
                    <AbilitiesRow label="Awareness" levels={domainData.awareness} />
                    <AbilitiesRow label="Sensitivity" levels={domainData.sensitivity} />
                    <AbilitiesRow label="Creativity" levels={domainData.creativity} />
                </div>
            </div>

            {/* Observational Notes */}
            <div className="p-2 bg-white border-t border-gray-400">
                <p className="text-sm italic mb-1">{domainData.observational_notes || ''}</p>
                <div className="space-y-2.5">
                    <div className="border-b border-gray-400 h-1"></div>
                    <div className="border-b border-gray-400 h-1"></div>
                    <div className="border-b border-gray-400 h-1"></div>
                </div>
            </div>
        </div>
    );
};


// --- Main PartC Component ---
// FIXED: Render all domains in a single page instead of creating nested report-page divs
// This prevents the duplicate Part C issue in PDF export

const PartC = ({ data }) => {
    // Show a loading state if data isn't available yet.
    if (!data || data.length === 0) {
        return (
            <div className="report-page flex items-center justify-center">
                Loading Part C Data...
            </div>
        );
    }

    // As per the images, define a fixed order for domains.
    const DOMAIN_ORDER = [
        "Language (R1)", "Language (R2)", "Mathematics", "The World Around Us", "Art Education", "Physical Education"
    ];

    const sortedData = [...data].sort((a, b) => {
        const indexA = DOMAIN_ORDER.indexOf(a.domain_name);
        const indexB = DOMAIN_ORDER.indexOf(b.domain_name);
        return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
    });

    return (
        <div className="report-page bg-white font-[poppins]" style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.5',
            color: '#333333',
            boxSizing: 'border-box'
        }}>
            {/* Header - only shown once */}
            <div className="text-center mb-4">
                <h1 className="text-2xl font-bold text-orange-600">PART C</h1>
                <h2 className="text-lg font-bold mb-2">SUMMARY FOR THE ACADEMIC YEAR</h2>
                <p className="text-sm text-left">
                    Tick the appropriate performance level descriptor and write an observational note for
                    each category based on performance throughout the academic year.
                </p>
            </div>

            {/* Render all domains in a single container */}
            {sortedData.map(domain => (
                <DomainSection key={domain.domain_id} domainData={domain} />
            ))}
        </div>
    );
};

export default PartC;